// src/components/Control/Operator/CameraTrack.tsx
// Слежение за объектом и индикатор записи — поверх кадра камеры 1.
//   robot/<id>/track/cmd   <- {"cmd":"start","box":[x0,y0,x1,y1]} (доли кадра) | {"cmd":"stop"}
//   robot/<id>/track/state -> TrackState (telemetry)
// В углу кадра — полупрозрачная панель режима: «Обзор» / «Выделить»; выделили
// рамкой объект — «Следить»; пока следит — состояние и «Стоп». Там же — «● Запись»,
// если идёт запись с камеры (нажатие — раздел «Запись с камеры»).
import React, { useEffect, useRef, useState } from 'react';
import { useRobot, useTelemetryState, useUiActions } from '../robot/RobotContext';
import { STALE, TrackState, fresh } from '../robot/telemetry';
import { putJson } from '../../../hooks/useZenohSubscription';
import { useTick } from '../../../hooks/useTick';

type Box = [number, number, number, number];
const MIN_SIZE = 0.03;  // меньше — случайный клик, а не рамка

const STATUS_TEXT: Record<string, string> = {
  starting: 'Начинаю…', tracking: 'Слежу', lost: 'Потерял — ищу',
};

function boxStyle(b: number[]): React.CSSProperties {
  return { left: `${b[0] * 100}%`, top: `${b[1] * 100}%`, width: `${(b[2] - b[0]) * 100}%`, height: `${(b[3] - b[1]) * 100}%` };
}

function useTrack(): TrackState | null {
  useTick(1000);
  const { track, at } = useTelemetryState(t => ({ track: t.track, at: t.trackAt }));
  return track && fresh(at, STALE.node) ? track : null;
}

// Рамка выделения мышью / пальцем
const SelectLayer: React.FC<{ onBox: (b: Box | null) => void; draft: Box | null }> = ({ onBox, draft }) => {
  const ref = useRef<HTMLDivElement>(null);
  const start = useRef<[number, number] | null>(null);
  const [drag, setDrag] = useState<Box | null>(null);
  const pos = (e: React.PointerEvent): [number, number] => {
    const r = ref.current!.getBoundingClientRect();
    return [Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)), Math.min(1, Math.max(0, (e.clientY - r.top) / r.height))];
  };
  const toBox = (a: [number, number], b: [number, number]): Box =>
    [Math.min(a[0], b[0]), Math.min(a[1], b[1]), Math.max(a[0], b[0]), Math.max(a[1], b[1])];
  const shown = drag ?? draft;
  return (
    <div ref={ref} className="op-track-select" aria-label="Выделите объект рамкой"
         onPointerDown={e => {
           e.stopPropagation();
           (e.target as HTMLElement).setPointerCapture(e.pointerId);
           start.current = pos(e);
           setDrag(null);
         }}
         onPointerMove={e => { if (start.current) setDrag(toBox(start.current, pos(e))); }}
         onPointerUp={e => {
           e.stopPropagation();
           if (!start.current) return;
           const b = toBox(start.current, pos(e));
           start.current = null;
           setDrag(null);
           onBox(b[2] - b[0] >= MIN_SIZE && b[3] - b[1] >= MIN_SIZE ? b : null);
         }}
         onPointerCancel={() => { start.current = null; setDrag(null); }}
         onClick={e => e.stopPropagation()}>
      {shown && <div className="op-track-box draft" style={boxStyle(shown)} />}
      {!shown && <div className="op-track-hint">Обведите объект рамкой</div>}
    </div>
  );
};

const RecordChip: React.FC = () => {
  useTick(1000);
  const { openSettings } = useUiActions();
  const { video, at } = useTelemetryState(t => ({ video: t.video, at: t.videoAt }));
  if (!video || !fresh(at, STALE.node) || video.status === 'off') return null;
  const rec = video.status === 'recording';
  const text = rec ? 'Запись' : video.status === 'waiting' ? 'Ждёт движения' : video.status === 'disk_full' ? 'Диск полон' : 'Запись: пауза';
  return (
    <button type="button" className={`op-cam-chip rec ${rec ? 'on' : ''}`} title="Запись с камеры: настройки и просмотр"
            onClick={e => { e.stopPropagation(); openSettings('recording'); }}>
      <span className="dot" aria-hidden="true">●</span>{text}
    </button>
  );
};

// Наложение для камеры 1: рамка слежения + панель режима в углу
export const CameraTrack: React.FC = () => {
  const { session, robotId, notify } = useRobot();
  const track = useTrack();
  const [mode, setMode] = useState<'view' | 'select'>('view');
  const [draft, setDraft] = useState<Box | null>(null);
  const send = (msg: object) => putJson(session, `robot/${robotId}/track/cmd`, msg);
  const following = !!track?.active;

  const start = () => {
    if (!draft) return;
    send({ cmd: 'start', box: draft.map(v => Math.round(v * 10000) / 10000) })
      .then(ok => { if (!ok) notify('Не удалось отправить команду', 'error'); });
    setDraft(null);
    setMode('view');
  };
  const stop = () => send({ cmd: 'stop' });
  const cancelSelect = () => { setDraft(null); setMode('view'); };

  // Почему слежение остановилось («объект потерян», «робот уснул») — несколько секунд
  const [stopped, setStopped] = useState<{ text: string; until: number } | null>(null);
  const wasActive = useRef(false);
  useEffect(() => {
    if (wasActive.current && track && !track.active && track.reason) setStopped({ text: track.reason, until: Date.now() + 8000 });
    if (track) wasActive.current = track.active;
  }, [track?.active, track?.reason]); // eslint-disable-line react-hooks/exhaustive-deps

  const status = track?.status ?? 'idle';
  return (
    <>
      {following && track?.box && (
        <div className={`op-track-box ${status}`} style={boxStyle(track.box)} aria-hidden="true">
          <span>{STATUS_TEXT[status] ?? ''}</span>
        </div>
      )}
      {mode === 'select' && <SelectLayer draft={draft} onBox={setDraft} />}
      <div className="op-cam-corner" onClick={e => e.stopPropagation()} onPointerDown={e => e.stopPropagation()}
           role="toolbar" aria-label="Режим камеры">
        {following ? (
          <>
            <span className={`op-cam-chip track ${status}`} role="status"
                  title={track?.pan_tilt ? 'Камера поворачивается за объектом' : 'Поворотной камеры нет — робот только отмечает объект'}>
              ◎ {STATUS_TEXT[status] ?? 'Слежу'}{status === 'lost' && track ? ` ${Math.round(track.lost_s)} с` : ''}
              {!track?.pan_tilt && ' · без поворота'}
            </span>
            <button type="button" className="op-cam-chip" onClick={stop}>Стоп</button>
          </>
        ) : mode === 'select' ? (
          <>
            {draft
              ? <button type="button" className="op-cam-chip primary" onClick={start} disabled={!session}>◎ Следить</button>
              : <span className="op-cam-chip muted">Выделите объект</span>}
            <button type="button" className="op-cam-chip" onClick={cancelSelect} aria-label="Отменить выделение">✕</button>
          </>
        ) : (
          <div className="op-cam-modes" role="radiogroup" aria-label="Режим">
            <button type="button" role="radio" aria-checked className="op-cam-chip on">Обзор</button>
            <button type="button" role="radio" aria-checked={false} className="op-cam-chip"
                    title={track ? 'Выделить объект рамкой и следить за ним камерой' : 'Нода tracker на роботе не отвечает'}
                    onClick={() => setMode('select')} disabled={!session || !track}>
              ⬚ Выделить
            </button>
          </div>
        )}
        {!following && stopped && Date.now() < stopped.until && mode === 'view' && (
          <span className="op-cam-chip muted" role="status">{stopped.text}</span>
        )}
        <RecordChip />
      </div>
    </>
  );
};
